import type { ThunkDispatch } from "@reduxjs/toolkit";
import type { UnknownAction } from "redux";
import {
  clearAllCaches,
  clearKeyValueCache,
  deleteDatabase,
  getKeyValueCache,
  storeKeyValueCache,
} from "../indexedDB";

import { apis } from '../api';
import { trackingApi } from '../api/trackingApi';
import { clearSelectedProject } from '../slices/projectSlice';
import { clearSelectedRelease } from '../slices/releaseSlice';
import { clearApiState } from '../slices/apiStateSlice';
import {
  addUser,
  setCurrentUser,
  updateUserData,
  removeUser,
  removeCurrentUser,
} from "../slices/authSlice";
import { UsersType } from "@types";

import { actions } from "../constants/appActions";

interface ActionExecutionProps {
  action: (typeof actions)[keyof typeof actions];
  dispatch: ThunkDispatch<any, any, UnknownAction>;
  args?: {
    userid?: number;
    user?: UsersType.UserPartial;
    token?: string;
    delay?: number;
    apis?: MinimalApi[];
  };
}

type MinimalApi = { util: { resetApiState: () => UnknownAction } };

const resetApi = async (
  dispatch: ThunkDispatch<any, any, UnknownAction>,
  apisList: MinimalApi[]
) => {
  for (const api of apisList) {
    dispatch(api.util.resetApiState());
  }
};

// Tracking reports and composer drafts are user-specific and must not survive
// logout or an account switch.
const clearTrackingState = (
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  dispatch(trackingApi.util.resetApiState());
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('karas.tracking.'))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable (private mode); nothing persisted.
  }
};

const resetSlice = async (dispatch: ThunkDispatch<any, any, UnknownAction>) => {
  dispatch(clearApiState());
  dispatch(clearSelectedRelease());
  dispatch(clearSelectedProject());
};

const resetApp = async (
  dispatch: ThunkDispatch<any, any, UnknownAction>,
  delay: number = 3000
) => {
  // Optional delay to show a toast
  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  // clear cache
  try {
    localStorage.clear();
    sessionStorage.clear();
    clearAllCaches();
    deleteDatabase();
    resetApi(dispatch, apis);
  } catch (e) {
    console.error(e);
  }

  // just reset redux don't reset anything ese
  dispatch({ type: "RESET_APP" });

  // Let the caller decide where to navigate via React Router
  return { resetDone: true };
};

const clearUser = async (
  dispatch: ThunkDispatch<any, any, UnknownAction>,
  userid: number
) => {
  await clearKeyValueCache(`token_${userid}`);
  //FIXME: REMOVE THIS LINE - UNSAFE LOCAL STORAGE USAGE
  localStorage.removeItem(`token_${userid}`);
  dispatch(removeUser(userid));
};

export const actionExecution = async ({
  action,
  dispatch,
  args,
}: ActionExecutionProps) => {
  switch (action) {
    case actions.resetCode:
      await dispatch(clearApiState());
      break;

    case actions.resetApp:
      await resetApp(dispatch, args?.delay);
      break;

    case actions.resetApi:
      await dispatch(clearApiState());
      await resetApi(dispatch, args?.apis ?? []);
      break;

    case actions.resetSlice:
      await resetSlice(dispatch);
      break;

    case actions.resetApiSlice:
      await resetApi(dispatch, args?.apis ?? []);
      await resetSlice(dispatch);
      break;

    case actions.resetApiStatusSlice:
      await dispatch(clearApiState());
      break;

    case actions.refresh:
      await resetApi(dispatch, args?.apis ?? []);
      await resetSlice(dispatch);
      // soft refresh no hard reload
      break;

    case actions.login:
      await storeKeyValueCache(`token_${args?.userid}`, args?.token!);
      //FIXME: REMOVE THIS LINE - UNSAFE LOCAL STORAGE USAGE
      localStorage.setItem("token", args?.token!);
      await dispatch(addUser({ user: args?.user! }));
      await dispatch(setCurrentUser({ userid: args?.userid! }));
      break;

    case actions.switchUser:
      {
        await dispatch(setCurrentUser({ userid: args?.userid! }));
        await resetApi(dispatch, args?.apis ?? []);
        clearTrackingState(dispatch);
        await resetSlice(dispatch);
      }
      break;

    case actions.updateUser:
      await dispatch(
        updateUserData({ userid: args?.userid!, user: args?.user! })
      );
      break;

    case actions.logout:
      await clearUser(dispatch, args?.userid!);
      await resetApi(dispatch, args?.apis ?? []);
      clearTrackingState(dispatch);
      await resetSlice(dispatch);
      break;

    case actions.logoutCurrent:
      await clearKeyValueCache(`token_${args?.userid}`);
      localStorage.removeItem(`token_${args?.userid}`);
      await dispatch(removeCurrentUser());
      await resetApi(dispatch, args?.apis ?? []);
      clearTrackingState(dispatch);
      await resetSlice(dispatch);

      // Add delay if specified
      if (args?.delay && args.delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, args.delay));
      }
      break;

    case actions.mfaRequired:
    default:
      return;
  }
};
